import storage from "./storage"
import { Loan, LoanStatus, LoanType } from "../types/loan";
import { UserProfile } from "../types/user"

export const loanPurposes: LoanPurpose[] = [
  "consumer",
  "travel",
  "home",
  "education",
  "health",
  "business",
  "vehicle",
  "refinancing",
]

export const businessLoanPurposes: LoanPurpose[] = [
  "equipment",
  "pay_off_non_tax_debt",
  "marketing",
  "purchasing_inventory",
  "pay_off_tax_debt",
  "working_capital",
  "pay_suppliers",
  "renovation",
  "development",
  "other",
];

export const getLoanPurposes = (loanType?: LoanType) => {
  return loanType === "business" ? businessLoanPurposes : loanPurposes;
};

export const getObligationsTypes = (loanType?: LoanType) => {
  return loanType === "business" ? businessObligationTypes : obligationTypes;
};

export const baseObligationTypes = [
  "car",
  "consumer",
  "other",
  "real_estate",
]

export const obligationTypes = [
  ...baseObligationTypes,
  "small_loan",
]

export const businessObligationTypes = [
  ...baseObligationTypes,
  "business",
]

const interestPerMonth = 0.18 / 12

const loanStorageKey = "loanData"

const initialLoan: Loan = {
  amount: 1800,
  id: null,
  loanType: "personal",
  minRefinanceAmount: 500,
  scoringStatus: "manual",
  status: "draft",
  period: 36,
  purpose: "consumer",
}

const businessInitialLoan: Loan = {
  amount: 2500,
  id: null,
  loanType: "business",
  minRefinanceAmount: 1000,
  period: 24,
  purpose: "equipment",
  scoringStatus: "manual",
  status: "draft",
};

export const getLoanStepsForAuth = (loan: Loan): LoanStep[] => {
  if (loan.scoringStatus === "autoAccepted") {
    return ["personal", "cashflow", "additional", "documents"];
  }
  if (loan.loanType === "business") {
    return ["personal", "verification", "company", "cashflow", "documents", "security"];
  }
  return ["personal", "verification", "cashflow", "additional", "documents"];
}

export function getNextStep(current: LoanStep, loan: Loan, profile: UserProfile): LoanStep | null {
  if (loan.loanType === "business") {
    switch(current) {
      case "initial":
        return "personal";
      case "personal":
        return profile.authProvider === "email" ? "verification" : "company";
      case "verification":
        return "company";
      case "company":
        return "cashflow";
      case "cashflow":
        return "documents";
      case "documents":
        return "security";
      case "security":
        return "thankyou";
    }
  }

  switch(current) {
    case "initial":
      return "personal"
    case "personal":
      return profile.authProvider === "email" ? "verification" : "cashflow"
    case "verification":
      return "cashflow"
    case "cashflow":
      return "additional";
    case "additional":
      return "documents"
    case "documents":
      return "thankyou"
  }
  return null
}

export function getPrevStep(current: LoanStep, loan: Loan, profile: UserProfile): LoanStep | null {
  if (loan.loanType === "business") {
    switch (current) {
      case "personal":
        return "initial";
      case "verification":
        return "personal";
      case "security":
        return "documents";
      case "documents":
        return "cashflow";
      case "cashflow":
        return "company";
      case "company":
        return "verification";
      case "additional":
        return "security";
    }
  }
  switch (current) {
    case "personal":
      return "initial"
    case "verification":
      return "personal"
    case "cashflow":
      return profile.authProvider === "email" ? "verification" : "personal"
    case "additional":
      return "cashflow"
    case "documents":
      return "additional";
  }
  return null
}

export function calculateMonthlyPayment(amount: number, months: number): number {
  if (amount <= 0 || months <= 0) return 0

  const firstPart = Math.pow(1 + interestPerMonth, months) - 1
  const secondPart = interestPerMonth / firstPart
  return amount * (interestPerMonth + secondPart)
}

export function calculateMaxLoanPeriod(amount: number, loanType: LoanType): number {
  if (loanType === "business") {
    return amount <= 1000 ? 24 : 36;
  }
  return 84;
}

export function calculateMaxLoanAmount(smallType: boolean, refinancingOnly?: boolean): number {
  if (smallType) return 1000;
  return refinancingOnly ? 10000 : 10000;
}

export function calculateRemainingAmountAfterRefinance(previousLoanDebt: number, fullInternalRefinanceFee: number, amount: number): number {
  return amount - previousLoanDebt - fullInternalRefinanceFee;
}

export function clearLocalLoan(): void {
  storage.remove(loanStorageKey)
}

export function getLocalLoan(): Loan | null {
  return storage.get(loanStorageKey)
}

export function getLocalOrNewLoan(loanType: LoanType): Loan {
  const localLoan = getLocalLoan();
  if (localLoan && localLoan.loanType !== loanType) {
    return loanType === "business" ? businessInitialLoan : initialLoan;
  }
  return localLoan ? localLoan : loanType === "business" ? businessInitialLoan : initialLoan;
}

export function getStepToRedirect(status: LoanStatus | "no_loan", step: LoanStep): LoanStep | "proposal" | null {
  if (status === "no_loan" && step !== "initial") return "initial";
  if (status === "prepared" && step !== "thankyou") return "thankyou";
  if (status === "fulfilled") return "proposal";
  return null;
}

export function hasProgressBar(loan: Loan | null, step: LoanStep): boolean | null {
  return loan && loan.scoringStatus !== "autoRejected" && step !== "thankyou"
}

export function saveLocalLoan(loan: Loan): void {
  storage.set(loanStorageKey, loan)
}
